import React, { useState } from 'react';
import { CheckCircle2, Circle, X } from 'lucide-react';
import { cn } from '../utils/cn';
import { Card, IconButton } from './ui';

export interface SetupChecklistItem {
  id: string;
  label: string;
  done: boolean;
  optional?: boolean;
  action?: React.ReactNode;
}

export interface SetupChecklistProps {
  items: SetupChecklistItem[];
  onDismiss: () => void;
  className?: string;
}

const DISMISSED_KEY = 'sw-setup-dismissed';

/**
 * "Einrichtung" card on Suchen: replaces the old stacked Passkey + e-mail
 * verification banners with a single, dismissible checklist. Hides itself
 * once every non-optional item is done, and stays dismissed across reloads
 * (localStorage) once the user closes it.
 */
export const SetupChecklist: React.FC<SetupChecklistProps> = ({ items, onDismiss, className }) => {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISSED_KEY) === '1';
    } catch {
      return false;
    }
  });

  const required = items.filter((item) => !item.optional);
  const doneCount = required.filter((item) => item.done).length;
  const allRequiredDone = required.length > 0 && doneCount === required.length;

  if (dismissed || allRequiredDone) return null;

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      // Storage may be unavailable (private mode); dismissal just won't persist.
    }
    setDismissed(true);
    onDismiss();
  };

  return (
    <Card padding="md" className={cn('mb-6', className)}>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold text-ink">Einrichtung</h2>
          <p className="mt-0.5 text-sm text-muted">
            {doneCount} von {required.length} erledigt
          </p>
        </div>
        <IconButton
          label="Einrichtung ausblenden"
          icon={<X className="h-4 w-4" />}
          variant="ghost"
          size="sm"
          onClick={handleDismiss}
          className="-m-1 shrink-0"
        />
      </div>
      <ul className="flex flex-col gap-3">
        {items.map((item) => (
          <li key={item.id} className="flex flex-wrap items-center justify-between gap-2">
            <span className="flex items-center gap-2 text-sm text-ink">
              {item.done ? (
                <CheckCircle2 className="h-5 w-5 shrink-0 text-success-text" aria-hidden="true" />
              ) : (
                <Circle className="h-5 w-5 shrink-0 text-muted" aria-hidden="true" />
              )}
              <span>
                {item.label}
                {item.optional && <span className="text-muted"> (optional)</span>}
              </span>
            </span>
            {!item.done && item.action}
          </li>
        ))}
      </ul>
    </Card>
  );
};
